import { Fragment, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import StarIcon from '@mui/icons-material/Star';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Field, LinkField, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { useRouter } from 'next/router';
import {
  SITE_MASTER_COLORS,
  StSiteMasterHeroContainer,
  StSiteMasterHeroWrapper,
  StSiteMasterHeroDetails,
  StSiteMasterHeroCtaGroup,
  StSiteMasterHeroImage,
  StSiteMasterHeroLeft,
  StSiteMasterHeroTagList,
  StSiteMasterChip,
} from 'components/SiteMaster/SiteMaster.styles';
import {
  getFieldValue,
  getMultilistDisplayFields,
  getSitePhoneNumber,
  extractNumberFromString,
  useSiteMasterRouteFields,
  useSiteRecruitingStudiesProps,
} from 'components/SiteMaster/utils';
import SiteBreadcrumb from './SiteBreadcrumb';
import { mergeTag, scrollToSectionPath } from 'lib/utils/common';
import ButtonSecondary from 'components/shared/Button/SecondaryButton';
import UiHeading from 'components/shared/Heading';
import CustomNextImage from 'components/shared/CustomNextImage/CustomNextImage';
import { fetchProfileVerifyTokenKey, setchProfileVerifyTokenKey } from '../../lib/utils/common';
import { saveSiteToProfile } from '../../services/RegisterUserService';
import { fetchProfileDetailsForCR } from '../../lib/utils/axiosCDPFetcher';
import { hideLoader, showLoader } from '../../features/loaderSlice';
import {
  setProfileData,
  clearPendingSaveSite,
  setPendingSaveSite,
  updateMyProfileModal,
  updateMyProfileModalContext,
} from '../../features/crProfileSlice';
import { SendEmailCategories } from '../../lib/type/common';
import { RootState } from '../../store/store';
import {
  getPreferredSiteTokenFromQuery,
  getSavedSiteIds,
  hasSavedSiteIdsField,
  SaveSiteModalItem,
  withUpdatedSavedSiteIds,
} from '../../lib/utils/saveSite';
import UiBox from '../shared/Box';

type SiteHeroBannerProps = ComponentProps & {
  fields: {
    backGroundColor?: Field<string>;
    callSiteText?: Field<string>;
    saveSiteText?: Field<string>;
    registerText?: Field<string>;
    savedSiteText?: Field<string>;
    savedSiteTooltipText?: Field<string>;
    establishmentYearFormat?: Field<string>;
    enableBreadcrumb?: Field<boolean>;
    breadcrumbLabel?: Field<string>;
    breadcrumbUrl?: LinkField;
    saveSiteModal?: SaveSiteModalItem;
    emailSent?: SaveSiteModalItem;
  };
};

const formatHeroEstablishmentYear = (rawYear?: string, format?: string): string => {
  const yearFormat = format?.trim() ?? '';
  const year = extractNumberFromString(rawYear);
  if (!yearFormat || year === null || year < 1000 || year > 9999) {
    return '';
  }
  return mergeTag(yearFormat, { year: String(year) });
};

const SiteHeroBanner = (props: SiteHeroBannerProps): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isAuthenticated: boolean = useSelector(
    (state: RootState) => state.authentication.isAuthenticated
  );
  const router = useRouter();
  const routeFields = useSiteMasterRouteFields();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  // Single owner: SiteRecruitingStudies getStaticProps
  const { hasRecruitingStudies } = useSiteRecruitingStudiesProps();
  const { fields } = props;
  const isSiteDetailPage = Boolean(
    (sitecoreContext?.route?.fields?.isSiteDetailPage as Field)?.value
  );

  useEffect(() => {
    dispatch(hideLoader());
  }, [dispatch]);

  const siteName = getFieldValue(routeFields.siteName);
  const siteId = getFieldValue(routeFields.siteId);
  const siteNumber = getSitePhoneNumber(routeFields, siteSettings);
  const hasHeroImage = [
    routeFields.siteImageDesktop,
    routeFields.siteImageTablet,
    routeFields.siteImageMobile,
    routeFields.siteImage,
  ].some((field) => field?.value?.src);
  const languageFields = getMultilistDisplayFields(routeFields.siteSupportedLanguages);
  const establishmentYear = formatHeroEstablishmentYear(
    getFieldValue(routeFields.siteEstablishmentYearText),
    getFieldValue(fields.establishmentYearFormat)
  );

  const heroTags: { key: string; content: JSX.Element }[] = [];

  if (getFieldValue(routeFields.siteType)) {
    heroTags.push({ key: 'siteType', content: <Text field={routeFields.siteType} /> });
  }
  if (establishmentYear) {
    heroTags.push({ key: 'establishmentYear', content: <>{establishmentYear}</> });
  }
  if (getFieldValue(routeFields.sitePhase)) {
    heroTags.push({ key: 'sitePhase', content: <Text field={routeFields.sitePhase} /> });
  }
  if (languageFields.length) {
    heroTags.push({
      key: 'siteSupportedLanguages',
      content: (
        <>
          {languageFields.map((languageField, index) => (
            <Fragment key={`${getFieldValue(languageField)}-${index}`}>
              {index > 0 && ' / '}
              <Text field={languageField} />
            </Fragment>
          ))}
        </>
      ),
    });
  }

  const heroBackground =
    getFieldValue(fields.backGroundColor) ||
    theme.palette.themeColor?.main ||
    SITE_MASTER_COLORS.teal;

  const noStudiesAvailableText = getFieldValue(siteSettings?.noStudiesAvailableText);
  const showNoStudiesMessage = !hasRecruitingStudies && Boolean(noStudiesAvailableText);
  const callSiteText = getFieldValue(fields.callSiteText);
  const showRegistrationCta = !isAuthenticated;
  const registerText = getFieldValue(fields.registerText);
  const saveSiteText = getFieldValue(fields.saveSiteText);
  const savedSiteText = getFieldValue(fields.savedSiteText);
  const savedSiteTooltip = getFieldValue(fields.savedSiteTooltipText);
  const profileData = useSelector((state: RootState) => state.crProfile?.profileData);
  const savedSiteIds = getSavedSiteIds(profileData);
  const isSiteSaved = Boolean(siteId) && savedSiteIds.includes(String(siteId));
  const hasSiteIdsInProfile = hasSavedSiteIdsField(profileData);
  const hasProcessedPreferredSiteToken = useRef(false);

  useEffect(() => {
    if (!isSiteDetailPage || !router.isReady || !siteId) {
      return;
    }

    const sitePageLink = (router.asPath || window.location.pathname).split('?')[0];
    dispatch(setPendingSaveSite({ siteId, pageLink: sitePageLink, siteName }));
  }, [dispatch, isSiteDetailPage, router.asPath, router.isReady, siteId, siteName]);

  useEffect(() => {
    const syncSavedState = async () => {
      const myProfileVerificationToken = fetchProfileVerifyTokenKey();
      if (!siteId || !myProfileVerificationToken || hasSiteIdsInProfile) {
        return profileData;
      }
      try {
        const { data } = await fetchProfileDetailsForCR(myProfileVerificationToken);
        if (data) {
          const nextProfileData = {
            ...profileData,
            ...data,
            siteIds: getSavedSiteIds(data),
          };
          dispatch(setProfileData(nextProfileData));
          return nextProfileData;
        }
      } catch (_error) {
        const nextProfileData = {
          ...profileData,
          siteIds: getSavedSiteIds(profileData),
        };
        dispatch(setProfileData(nextProfileData));
        return nextProfileData;
      }
      return profileData;
    };
    const saveSiteFromVerificationLink = async (currentProfileData: any) => {
      const preferredSiteToken = getPreferredSiteTokenFromQuery(router.query);

      if (
        !isSiteDetailPage ||
        !router.isReady ||
        !siteId ||
        !preferredSiteToken ||
        hasProcessedPreferredSiteToken.current
      ) {
        return;
      }

      hasProcessedPreferredSiteToken.current = true;
      try {
        dispatch(showLoader());
        await setchProfileVerifyTokenKey(preferredSiteToken);
        if (fetchProfileVerifyTokenKey()) {
          await saveSiteToProfile(siteId, true, siteName);
          dispatch(setProfileData(withUpdatedSavedSiteIds(currentProfileData, siteId, true)));
          dispatch(clearPendingSaveSite());
        }
      } finally {
        dispatch(hideLoader());
      }
    };

    const run = async () => {
      const currentProfileData = await syncSavedState();
      await saveSiteFromVerificationLink(currentProfileData);
    };

    run();
  }, [
    dispatch,
    hasSiteIdsInProfile,
    isSiteDetailPage,
    profileData,
    router.isReady,
    router.query,
    siteId,
  ]);

  const handleSaveSiteClick = async () => {
    if (!siteId) {
      return;
    }

    if (!isAuthenticated) {
      const sitePageLink = (router.asPath || window.location.pathname).split('?')[0];
      dispatch(setPendingSaveSite({ siteId, pageLink: sitePageLink, siteName }));
      dispatch(
        updateMyProfileModalContext({
          emailType: SendEmailCategories.PREFERRED_SITE,
          preferredSiteId: siteId,
          preferredSitesPageLink: sitePageLink,
          preferredSiteName: siteName,
          modalContent: {
            saveSiteModal: fields.saveSiteModal,
            emailSent: fields.emailSent,
          },
        })
      );
      dispatch(updateMyProfileModal(true));
      return;
    }

    const nextIsSaved = !isSiteSaved;
    try {
      dispatch(showLoader());
      await saveSiteToProfile(siteId, nextIsSaved, siteName);
      dispatch(setProfileData(withUpdatedSavedSiteIds(profileData, siteId, nextIsSaved)));
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <StSiteMasterHeroContainer sx={{ background: heroBackground }}>
      <StSiteMasterHeroWrapper>
        <StSiteMasterHeroLeft>
          <StSiteMasterHeroDetails>
            {fields.enableBreadcrumb?.value ? (
              <SiteBreadcrumb
                label={fields.breadcrumbLabel}
                url={fields.breadcrumbUrl}
                siteName={routeFields.siteName}
              />
            ) : null}
            <UiHeading
              size="h1"
              color="white"
              isChildren
              className="site-hero-title"
              styles={{
                fontWeight: 400,
                fontSize: '48px',
                lineHeight: 'normal',
              }}
            >
              <Text field={routeFields.siteName} defaultValue={siteName} />
            </UiHeading>
            {heroTags.length > 0 && (
              <StSiteMasterHeroTagList>
                {heroTags.map((tag) => (
                  <StSiteMasterChip
                    key={tag.key}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {tag.content}
                  </StSiteMasterChip>
                ))}
              </StSiteMasterHeroTagList>
            )}
            {showNoStudiesMessage ? (
              <Typography
                sx={{
                  color: theme.palette.common.white,
                  fontWeight: 700,
                  fontSize: '19px',
                  lineHeight: 1.4,
                }}
              >
                <Text field={siteSettings?.noStudiesAvailableText} />
              </Typography>
            ) : null}
            <StSiteMasterHeroCtaGroup>
              {showRegistrationCta && (
                <ButtonSecondary
                  id="site-hero-registration-button"
                  gap="4px"
                  iconRight={<PersonOutlineIcon />}
                  onClick={() => scrollToSectionPath('#cr-registration', 0, 500)}
                  type="button"
                  aria-label={registerText || undefined}
                >
                  <Text field={fields.registerText} />
                </ButtonSecondary>
              )}
              <UiBox styles={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {isSiteSaved ? (
                  <Tooltip title={savedSiteTooltip} arrow>
                    <span>
                      <ButtonSecondary
                        className="site-hero-icon-button"
                        aria-label={savedSiteText || undefined}
                        iconRight={<StarIcon />}
                        onClick={handleSaveSiteClick}
                        type="button"
                      >
                        <span className="site-hero-button-label">{savedSiteText}</span>
                      </ButtonSecondary>
                    </span>
                  </Tooltip>
                ) : (
                  <ButtonSecondary
                    className="site-hero-icon-button"
                    aria-label={saveSiteText || undefined}
                    iconRight={<StarBorderOutlinedIcon />}
                    onClick={handleSaveSiteClick}
                    type="button"
                  >
                    <span className="site-hero-button-label">
                      <Text field={fields.saveSiteText} />
                    </span>
                  </ButtonSecondary>
                )}
                {siteNumber && (
                  <ButtonSecondary
                    id="site-hero-call-button"
                    className="site-hero-icon-button"
                    aria-label={callSiteText}
                    gap="4px"
                    iconRight={<PhoneOutlinedIcon />}
                    href={`tel:${siteNumber}`}
                    target="_blank"
                  >
                    <span className="site-hero-button-label">
                      <Text field={fields.callSiteText} />
                    </span>
                  </ButtonSecondary>
                )}
              </UiBox>
            </StSiteMasterHeroCtaGroup>
          </StSiteMasterHeroDetails>
        </StSiteMasterHeroLeft>
        {hasHeroImage && (
          <StSiteMasterHeroImage>
            <CustomNextImage
              mobileImg={routeFields.siteImageMobile}
              tabletImg={routeFields.siteImageTablet}
              desktopImg={routeFields.siteImageDesktop ?? routeFields.siteImage}
              objectFit="cover"
              objectPosition="center"
              priority
              ariaLabel={siteName}
            />
          </StSiteMasterHeroImage>
        )}
      </StSiteMasterHeroWrapper>
    </StSiteMasterHeroContainer>
  );
};

export default SiteHeroBanner;
